import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import SeoHeader from "../../../components/seo/SeoHeader.jsx";
import SeoContactSection from "../../../components/seo/SeoContactSection.jsx";
import SeoWorkSteps from "../../../components/seo/SeoWorkSteps.jsx";
import CountryBanner from "../../../components/CountryBanner.jsx";

import guest1 from "../../../img/seo/important_1.png";
import guest2 from "../../../img/seo/important_2.png";
import guest3 from "../../../img/seo/important_3.png";
import guest4 from "../../../img/seo/important_4.png";
import guest5 from "../../../img/seo/important_5.png";

const navItems = [
  {
    id: "What-mean",
    label: "Hva er en flerspråklig meny for restauranter?",
  },
  {
    id: "foreign-language",
    label:
      "Menyer på fremmedspråk for restauranter: en enklere måte å betjene internasjonale gjester på",
  },
  {
    id: "checklist",
    label: "Trenger restauranten din en flerspråklig meny?",
  },
  {
    id: "Why-important",
    label: "Hvorfor er en flerspråklig meny viktig?",
  },
  {
    id: "reduces",
    label: "Språk reduserer usikkerhet før bestilling",
  },
  {
    id: "languages",
    label: "Hvilke språk bør en restaurant tilby?",
  },
  {
    id: "automatic",
    label: "Hvorfor er automatisk oversettelse ikke alltid nok?",
  },
  {
    id: "qr",
    label: "Hvorfor QR-kode-menyer gjør flerspråklige menyer enklere",
  },
  {
    id: "mistakes",
    label: "Vanlige feil med flerspråklige menyer",
  },
  {
    id: "bestplace",
    label: "Hvordan Best Place hjelper deg med å starte en QR-meny",
  },
  {
    id: "faq",
    label: "Vanlige spørsmål",
  },
];

const acquaintancePhotos = [
  {
    src: guest1,
    alt: "Gjest som skanner en QR-meny i en restaurant",
  },
  {
    src: guest2,
    alt: "Digital restaurantmeny på en smarttelefon",
  },
  {
    src: guest3,
    alt: "Restaurantgjest som velger en rett online",
  },
  {
    src: guest4,
    alt: "Restauranteier som redigerer menyen",
  },
  {
    src: guest5,
    alt: "Online bestilling og betaling i en restaurant",
  },
];

const CALLBACK_API_ORIGIN =
  typeof window !== "undefined" &&
  (window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1")
    ? "http://localhost:3030"
    : "";

const checklist = [
  "Restauranten din betjener regelmessig internasjonale gjester.",
  "Turister spør ofte etter engelsk eller et annet språk.",
  "Menyen inneholder regionale retter som er vanskelige å forklare.",
  "Gjester spør ofte om ingredienser, allergener eller tilberedning.",
  "Du vil redusere misforståelser når gjester bestiller.",
  "Du vil gjøre menyen tydeligere online og via QR-kode.",
  "Restauranten ligger i en turistby eller nær hoteller.",
  "Du vil tilby flere språk uten å trykke flere papirmenuer.",
  "Du vil gi et mer profesjonelt inntrykk til internasjonale gjester.",
];

const faqLinks = [
  {
    question: "Må gjesten installere en app for å åpne en QR-meny?",
    answer:
      "Nei. En god QR-meny bør åpnes direkte i nettleseren på telefonen. Det gjør opplevelsen enkel og fjerner en av de største hindringene for gjester.",
    linkText: "Les mer om QR-menyer uten app",
    to: "/no/qr-kode-meny-uten-app",
  },
  {
    question: "Hva er den beste måten å oversette en restaurantmeny på?",
    answer:
        "Det kommer an på hvor ofte menyen endres og hvor mange internasjonale gjester restauranten har. Papirmenuer kan fungere for stabile menyer. AI kan hjelpe med første utkast. En flerspråklig QR-meny er vanligvis mer fleksibel når du trenger flere språk og jevnlige oppdateringer.",
    linkText: "Les mer om å oversette restaurantmenyer",
    to: "/no/oversette-restaurantmeny",
    },
  {
    question: "Kan jeg teste en digital meny gratis før jeg bestemmer meg?",
    answer:
      "Ja. For mange restauranter er det smarteste første steget ikke et stort teknisk prosjekt, men en enkel test: opprett en meny, skann QR-koden og se hvordan den fungerer på en ekte telefon.",
    linkText: "Les mer om å teste en gratis digital meny",
    to: "/no/test-gratis-digital-meny",
  },
  {
    question: "Kan jeg oppdatere menyen på nettet uten å trykke den på nytt?",
    answer:
      "Ja. Dette er en av de mest praktiske grunnene til å bruke en digital meny. Priser, utsolgte retter, dagens tilbud og bilder kan endres online i stedet for å lage en ny PDF eller trykke en ny meny.",
    linkText: "Les mer om å administrere menyen online",
    to: "/no/administrere-meny-online-for-restauranter",
  },
  {
    question: "Betyr bilder i en digital meny virkelig noe?",
    answer:
      "Ja, det kan bety mye, spesielt for retter, drikke, menyer, desserter og sesongtilbud. En gjest forstår ofte et tilbud raskere når det vises tydelig i stedet for bare å bli beskrevet med tekst.",
    linkText: "Les mer om menyer med bilder",
    to: "/no/meny-med-bilder",
  },
  {
    question: "Kan jeg bruke en QR-kode på bordskilt?",
    answer:
      "Ja. Bordskilt er et av de mest naturlige stedene for en QR-meny, fordi gjesten ser koden akkurat når han eller hun er klar til å velge noe.",
    linkText: "Les mer om QR-kode bordskilt",
    to: "/no/restaurant-qr-kode-bordskilt",
  },
  {
    question: "Kan en QR-meny fungere sammen med online bordreservasjon?",
    answer:
      "Ja. En digital meny kan være en del av et bredere restaurantsystem: meny, bordreservasjon, bilder, aktuelle tilbud og senere også bestillingsflyt.",
    linkText: "Les mer om online bordreservasjon",
    to: "/no/online-bordreservasjon-for-restauranter",
  },
  {
    question: "Er en digital meny nyttig for en pizzeria?",
    answer:
      "Ja, spesielt når du må vise størrelser, toppinger, ekstra valg, menyer, lunsjtilbud eller sesongkampanjer på en tydelig måte.",
    linkText: "Les mer om digitale menyer for pizzeriaer",
    to: "/no/digital-meny-pizzeria",
  },
];

function getResult(score) {
  if (score <= 3) {
    return {
      title: "Flerspråklige menyer er kanskje ikke førsteprioritet ennå",
      text:
        "Hvis gjestene dine nesten bare er lokale og menyen er enkel, kan én språkutgave være nok foreløpig.",
      tone: "low",
    };
  }

  if (score <= 6) {
    return {
      title: "Et ekstra språk er verdt å teste",
      text:
        "Restauranten din kan ha nytte av å legge til et ekstra språk, spesielt hvis gjester ofte spør om retter, ingredienser eller priser.",
      tone: "medium",
    };
  }

  return {
    title: "En flerspråklig meny kan være svært nyttig",
    text:
      "Flere punkter viser at restauranten din kan ha nytte av en flerspråklig meny: turister, tydeligere bestilling, færre spørsmål og et mer profesjonelt inntrykk.",
    tone: "high",
  };
}

export default function MultilingualMenuForRestaurantsNo() {
  const [checked, setChecked] = useState([]);

  const score = checked.length;
  const result = useMemo(() => getResult(score), [score]);

  useEffect(() => {
    document.documentElement.setAttribute("lang", "no");
    document.title =
      "Flerspråklig QR-meny for restauranter | Best Place";
  }, []);

  const toggle = (item) => {
    setChecked((prev) =>
      prev.includes(item) ? prev.filter((x) => x !== item) : [...prev, item]
    );
  };

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  return (
    <main className="qrArticle">
      <SeoHeader
        lang="no"
        langLinks={{
          de: "/de/mehrsprachige-speisekarte",
          en: "/en/multilingual-menu-for-restaurants",
          no: "/no/flerspraklig-meny-for-restauranter",
        }}
      />

      <section id="ready" className="qrArticle__hero">
        <div className="qrArticle__heroInner">
          <h1 className="qrArticle__heroTitle">
            <span>
              Flerspråklig meny for restauranter: enklere å forstå, enklere å
              bestille
            </span>
          </h1>

          <div className="qrArticle__topsection">
            <div className="qrArticle__topsection--img-wrapper">
              <div className="qrArticle__topsection--img"></div>
            </div>

            <div className="qrArticle__topsection--text-wrapper">
              <p>
                En flerspråklig meny er mer enn en oversettelse. Den hjelper
                gjester med å forstå retter tryggere, reduserer spørsmål og gjør
                bestillingen enklere.
              </p>

              <p>
                For internasjonale gjester, turister eller regionale
                spesialiteter kan språk påvirke om en gjest trygt prøver noe
                nytt — eller velger bare det han eller hun allerede kjenner.
              </p>
            </div>
          </div>
        </div>
      </section>

      <div className="countryBannerBlock">
        <CountryBanner
          reverse
          ariaLabel={"Countries and languages"}
        />
      </div>

      <SeoContactSection
        lang="no"
        apiOrigin={CALLBACK_API_ORIGIN}
        title="Trenger du hjelp med å lage en flerspråklig meny?"
      />

      <section className="articls">
        <h2>Innhold i artikkelen</h2>

        <nav className="qrArticle__nav" aria-label="Artikkelnavigasjon">
          <ol className="qrArticle__navList">
            {navItems.map((item, index) => (
              <li key={item.id} className="qrArticle__navItem">
                <button
                  type="button"
                  className="qrArticle__navButton"
                  onClick={() => scrollTo(item.id)}
                >
                  <span className="qrArticle__navNumber">
                    {String(index + 1).padStart(2, "0")}
                  </span>

                  <span className="qrArticle__navLabel">{item.label}</span>
                </button>
              </li>
            ))}
          </ol>
        </nav>
      </section>

      <article>
        <section id="What-mean" className="acquaintance">
          <h2>Hva er en flerspråklig meny for restauranter?</h2>

          <p>
            En flerspråklig meny viser navn på retter, beskrivelser, kategorier
            og viktig informasjon på flere språk. Vanligvis brukes det lokale
            språket som hovedversjon, med engelsk eller andre språk som
            tillegg.
          </p>

          <p>
            Teknisk fungerer det enkelt: gjesten skanner en QR-kode, og menyen
            åpnes som en webapp på telefonen. Det er alltid mulig å velge et
            annet språk. Dette er en stor fordel sammenlignet med tradisjonelle
            papirmenuer, fordi en slik språkbytte-funksjon er mye enklere i et
            digitalt format.
          </p>

          <p>
            I neste seksjon kan du svare på en kort sjekkliste og se om denne
            typen digital meny er nyttig for restauranten din.
          </p>
        </section>

        <section id="foreign-language" className="acquaintance">
          <h2>
            Menyer på fremmedspråk for restauranter: en enklere måte å betjene
            internasjonale gjester på
          </h2>

          <p>
            Menyer på fremmedspråk er ofte viktige før gjesten i det hele tatt
            setter seg ved bordet. Mange turister og internasjonale gjester
            sjekker restauranten online først. Hvis de ser at menyen finnes på
            et språk de forstår, føles restauranten enklere, tryggere og mer
            velkommen. For noen gjester kan dette påvirke hvilken restaurant de
            velger.
          </p>

          <p>
            Derfor bør menyer på fremmedspråk ikke være kompliserte for
            restauranteieren. Du trenger ikke lage separate PDF-er, redesigne
            trykte menyer eller bygge den samme menyen manuelt igjen og igjen.
            Med en digital flerspråklig meny kan prosessen være mye enklere:
            restauranten forbereder én tydelig menystruktur med navn på retter,
            beskrivelser, priser, kategorier og nødvendige språkversjoner.
          </p>

          <p>
            Dette gir restauranten én klar menystruktur og flere språkversjoner
            inni den. Gjester kan åpne menyen på telefonen, velge sitt språk,
            forstå retter raskere og bestille med mer trygghet. For teamet betyr
            det færre oversettelsesspørsmål, enklere oppdateringer og en bedre
            opplevelse for utenlandske gjester uten ekstra papirmenuer.
          </p>
        </section>

        <section id="checklist" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Trenger restauranten din en flerspråklig meny?</h2>

            <p>
              Denne korte sjekken hjelper deg med å vurdere om et andre eller
              tredje språk kan være nyttig for restauranten din.
            </p>

            <div className="qrArticle__checkGrid">
              {checklist.map((item) => (
                <label key={item} className="qrArticle__checkItem">
                  <input
                    type="checkbox"
                    checked={checked.includes(item)}
                    onChange={() => toggle(item)}
                  />
                  <span className="text">{item}</span>
                </label>
              ))}
            </div>

            <div className={`qrArticle__result qrArticle__result--${result.tone}`}>
              <div className="qrArticle__score">
                {score} / {checklist.length}
              </div>

              <div>
                <strong>{result.title}</strong>
                <p>{result.text}</p>
              </div>
            </div>
          </div>
        </section>

        <section id="Why-important" className="acquaintance">
          <h2>Hvorfor er en flerspråklig meny viktig?</h2>

          <p>
            I turistbyer og internasjonale forretningsregioner kan utenlandske
            gjester utgjøre en betydelig del av etterspørselen. Det betyr at
            internasjonale besøkende ikke nødvendigvis er et sjeldent unntak.
            Avhengig av beliggenhet kan de være en normal del av den daglige
            restaurantdriften.
          </p>

          <div className="picture__slider" aria-label="Fordeler med flerspråklig QR-meny">
            {acquaintancePhotos.map((photo, index) => (
              <div className="picture__slide" key={index}>
                <img src={photo.src} alt={photo.alt} />
              </div>
            ))}
          </div>

          <p>
            For restauranter betyr dette at språk ikke bare er en ekstra detalj.
            Det kan påvirke hvor trygt en gjest føler seg før bestilling, hvor
            mange spørsmål personalet må svare på, og om gjesten tør å velge en
            lokal eller ukjent rett.
          </p>

          <p>
            En flerspråklig meny kan også gi et mer profesjonelt inntrykk når
            gjesten møter restauranten online, via QR-kode eller direkte ved
            bordet.
          </p>
        </section>

        <section id="reduces" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Språk reduserer usikkerhet før bestilling</h2>

            <p>
              Når gjester ikke forstår en rett, bestiller de ofte mer forsiktig.
              De velger noe kjent, stiller flere spørsmål eller hopper over
              tillegg. En tydelig meny kan redusere denne usikkerheten.
            </p>

            <div className="multiMenuArticle__comparison">
              <div>
                <h3>Bare ett språk</h3>
                <ul className="qrArticle__block-li">
                  <li className="text__seo-li">
                    Gjester må kanskje gjette eller spørre.
                  </li>
                  <li className="text__seo-li">
                    Regionale uttrykk forblir uklare.
                  </li>
                  <li className="text__seo-li">
                    Allergener og ingredienser føles usikre.
                  </li>
                  <li className="text__seo-li">
                    Ekstra valg kan bli oversett.
                  </li>
                </ul>
              </div>

              <div className="multiMenuArticle__comparisonHighlight">
                <h3>Flerspråklig meny</h3>
                <ul className="qrArticle__block-li">
                  <li className="text__seo-li">
                    Mer trygghet ved bestilling.
                  </li>
                  <li className="text__seo-li">Færre spørsmål.</li>
                  <li className="text__seo-li">
                    Bedre forklaring av lokale retter.
                  </li>
                  <li className="text__seo-li">
                    Mer profesjonelt inntrykk for internasjonale gjester.
                  </li>
                </ul>
              </div>
            </div>

            <p>
              Målet er ikke å gjøre alle menytekster lange. Målet er å gi nok
              klarhet til at gjesten føler seg trygg når han eller hun velger.
            </p>
          </div>
        </section>

        <section id="languages" className="acquaintance">
          <h2>Hvilke språk bør en restaurant tilby?</h2>

          <p>
            For mange restauranter er den beste starten enkel: lokalt språk og
            engelsk. Deretter avhenger de riktige språkene av beliggenhet,
            målgruppe og hvilke gjester restauranten faktisk betjener.
          </p>

          <div className="multiMenuArticle__cardGrid">
            <div>
              <strong>Engelsk</strong>
              <p>ofte det mest nyttige første tilleggsspråket</p>
            </div>

            <div>
              <strong>Fransk</strong>
              <p>nyttig i områder med fransktalende gjester</p>
            </div>

            <div>
              <strong>Italiensk</strong>
              <p>interessant for turistområder eller italiensk kjøkken</p>
            </div>

            <div>
              <strong>Spansk</strong>
              <p>nyttig i internasjonale byer og turistområder</p>
            </div>

            <div>
              <strong>Nederlandsk</strong>
              <p>relevant i turistregioner med nederlandske gjester</p>
            </div>

            <div>
              <strong>Polsk</strong>
              <p>nyttig avhengig av region, publikum eller beliggenhet</p>
            </div>
          </div>

          <h3>Europa er fortsatt språklig mangfoldig</h3>

          <p>
            Selv om mange europeere kan kommunisere på et fremmedspråk, betyr
            ikke det at alle gjester leser en meny komfortabelt på samme språk.
            En meny bør være enkel å forstå i situasjonen der gjesten faktisk
            skal velge mat.
          </p>
        </section>

        <section id="automatic" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Hvorfor er automatisk oversettelse ikke alltid nok?</h2>

            <p>
              Automatisk oversettelse eller AI kan være en god start, men det
              har også ulemper når menyen skal fungere profesjonelt på flere
              språk.
            </p>

            <ul className="qrArticle__block-li">
              <li className="text__seo-li">
                Menyen kan laste tregere hvis oversettelser hentes dynamisk.
              </li>
              <li className="text__seo-li">
                Samme tekst kan få ulike oversettelser over tid.
              </li>
              <li className="text__seo-li">
                Kvalitetskontroll av rettbeskrivelser blir vanskeligere.
              </li>
            </ul>

            <p>
              Automatisk oversettelse kan hjelpe med første utkast. Men menyer
              er sensitive: regionale retter, tilberedningsmetoder, allergener,
              kjøtttyper, tilbehør og kulturelle uttrykk kan bli uklare eller
              misvisende.
            </p>

            <ul className="qrArticle__block-li">
              <li className="text__seo-li">Sjekk navn på retter.</li>
              <li className="text__seo-li">
                Oversett allergener ekstra nøye.
              </li>
              <li className="text__seo-li">
                Ikke skjul viktige ingredienser.
              </li>
              <li className="text__seo-li">
                Forklar styrkegrad og tilberedning.
              </li>
              <li className="text__seo-li">
                Gjør porsjoner og tilbehør tydelige.
              </li>
              <li className="text__seo-li">
                Vis priser og valg klart.
              </li>
            </ul>

            <p>
              En god menyoversettelse trenger ikke være poetisk. Den må hjelpe
              gjesten med å ta riktig beslutning.
            </p>
          </div>
        </section>

        <section id="qr" className="acquaintance">
          <h2>Hvorfor QR-kode-menyer gjør flerspråklige menyer enklere</h2>

          <p>
            Med trykte menyer kan hvert ekstra språk bety flere sider, mer
            layoutarbeid, mer trykking og mer vedlikehold. En digital QR-meny
            kan gjøre språkstyring mye mer fleksibel.
          </p>

          <ul className="qrArticle__block-li">
            <li className="text__seo-li">Skann QR-kode.</li>
            <li className="text__seo-li">Åpne menyen.</li>
            <li className="text__seo-li">Velg språk.</li>
            <li className="text__seo-li">Forstå rettene.</li>
            <li className="text__seo-li">Bestill med trygghet.</li>
          </ul>

          <p>
            Når gjester kan velge sitt eget språk, trenger ikke teamet å
            oversette hver detalj ved bordet. Personalet kan fokusere mer på
            service og gjestfrihet.
          </p>
        </section>

        <section id="mistakes" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Vanlige feil med flerspråklige menyer</h2>

            <div className="multiMenuArticle__mistakes">
              <div>
                <h3>1. Bare navn på retter oversettes</h3>
                <p>
                  Navnet er ofte ikke nok. Gjester kan også trenge ingredienser,
                  tilberedning eller informasjon om tilbehør.
                </p>
              </div>

              <div>
                <h3>2. Allergener oversettes unøyaktig</h3>
                <p>
                  Allergener og viktige merknader trenger ekstra forsiktighet på
                  hvert språk.
                </p>
              </div>

              <div>
                <h3>3. For mange språk på papir</h3>
                <p>
                  Trykte menyer kan raskt bli rotete når alle språk vises på
                  hver side.
                </p>
              </div>

              <div>
                <h3>4. Oversettelser blir ikke kontrollert</h3>
                <p>
                  Automatisk oversettelse kan hjelpe, men menytekster bør
                  fortsatt sjekkes.
                </p>
              </div>

              <div>
                <h3>5. Språkbytte er vanskelig å finne</h3>
                <p>
                  Hvis gjester ikke finner språkvalget, hjelper ikke
                  flerspråklig innhold mye.
                </p>
              </div>

              <div>
                <h3>6. Priser og valg er uklare</h3>
                <p>
                  Tilbehør, ekstra valg og tillegg i pris må være tydelige på
                  hvert språk.
                </p>
              </div>
            </div>
          </div>
        </section>

        <SeoWorkSteps lang="no" />

        <section id="faq" className="articls">
          <h2>Vanlige spørsmål</h2>

          <div className="qrArticle__faq">
            {faqLinks.map((item) => (
              <details key={item.to}>
                <summary>{item.question}</summary>

                <p>{item.answer}</p>

                <p>
                  <Link to={item.to}>{item.linkText}</Link>
                </p>
              </details>
            ))}
          </div>
        </section>
      </article>
    </main>
  );
}